import React, { useEffect, useState, useCallback, ReactNode, useMemo } from 'react';
import axios, { AxiosError } from 'axios';
import debounce from 'lodash/debounce';
import union from 'lodash/union';

import { makeStyles } from '@material-ui/styles';
import { MenuItem, Theme } from '@material-ui/core';
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    FilterOptionsState
} from '@material-ui/lab';
import { CheckBox, CheckBoxOutlineBlank } from '@material-ui/icons';

import TextField from '../../../../../../Common/UI/TextField';
import SelectTextField from '../../../../../../Common/UI/SelectTextField';

import {
    Filter,
    MultiStringProperties,
    StringFieldSettings
} from '../../../../../../Common/UI/Filter/Interfaces/Filter';

function MultiStringRule({ tree, field, onChange }: Filter): JSX.Element {
    const classes = useStyles();
    const [options, setOptions] = useState<string[]>([]);
    const [isFocused, setIsFocused] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>('');
    const treeProperties = tree.properties as MultiStringProperties;
    let selectedOpt = treeProperties?.values ?? [];
    const placeholder = handlePlaceHolder(selectedOpt);

    const empty = useMemo(() => [], []);

    function handlePlaceHolder(values: string[]): string {
        if (values.length === 1) return values[0];

        if (values.length > 1) return `${values.length} items selected`;

        return 'Value';
    }

    // Use callback is used to maintain the debouce between rerenders so that the functions don't get repeatably called
    const debouncedAutocomplete = useCallback(
        debounce((facetId: number, facetValue: string) => {
            setIsLoadingOptions(true);
            axios
                .get<string[]>(
                    `/api/facets/${facetId}/autocomplete?value=${facetValue}`
                )
                .then(response => {
                    // TODO - If autocomplete exact and matches options then valid needs to be set to true
                    setOptions(response.data);
                    setIsLoadingOptions(false);
                })
                .catch((error: AxiosError) => {
                    setIsLoadingOptions(false);
                    if (axios.isCancel(error)) {
                        console.log('cancel');
                    } else {
                        console.error(error);
                    }
                });
        }, 500),
        []
    );

    const debouncedHandleChange = useCallback(
        debounce((onHandleChange: (value: string) => void, value: string) => {
            onHandleChange(value);
        }, 1000),
        []
    );

    function handleChange(name: string, values: string | string[]): void {
        const newTree = { ...tree };
        newTree.properties = {
            ...newTree.properties,
            [name]: values
        };
        const newTreeProperties = newTree.properties as MultiStringProperties;
        const operator = newTreeProperties?.operator;
        const fieldValue = newTreeProperties?.values ?? [];

        operator === 'ilike' && fieldValue?.length > 0
            ? (newTree.isValid = true)
            : (newTree.isValid = false);

        if (operator === 'ilike' && fieldValue === undefined) {
            newTree.isValid = false;
        }

        onChange(newTree);
    }

    function handleTextFieldChange(
        event: React.ChangeEvent<HTMLInputElement>
    ): void {
        // console.error('Needs implementing')
        console.log(event.target.value);
    }

    function handleAutocompleteInputChange(
        event: object,
        value: string,
        reason: string
    ): void {
        if (reason === 'input') {
            setInputValue(value);
        }
        if (reason === 'reset' || value === '') return;

        setOptions([]);
        if (field.settings) {
            const stringFieldSettings = field.settings as StringFieldSettings;
            // If exact match is required for autocomplete then set is valid to false while typing
            // TODO - If matches existing options then can set to true
            // When using multiple multistring, we don't allow live updates
            // debouncedHandleChange(handleChange, value);
            if (stringFieldSettings.autocompleteFacetId) {
                // Don't bother autocompleting while there is no input
                if (value !== '') {
                    debouncedAutocomplete(
                        stringFieldSettings.autocompleteFacetId,
                        value
                    );
                }
            } else if (stringFieldSettings.autocompleteOptions) {
                setOptions(
                    stringFieldSettings.autocompleteOptions.filter(option =>
                        option.includes(value)
                    )
                );
            }
        }
    }

    function filterOptions(
        options: string[],
        { inputValue }: FilterOptionsState<string>
    ): string[] {
        const filtered = inputValue
            ? options.filter(option =>
                  option.toLowerCase().includes(inputValue.toLowerCase())
              )
            : [];
        return [...new Set([...selectedOpt, ...filtered])];
    }

    function handleAutocompleteChange(
        event: object,
        values: string[],
        reason: string
    ): void {
        debouncedHandleChange.cancel();
        selectedOpt = values;
        handleChange('values', values);
    }

    function handleKeyDown(event: any): void {
        if (event.key === 'Backspace') {
            event.stopPropagation();
        }
    }

    function handleOnBlur(event: any): void {
        setIsFocused(false);

        if (event.type === 'blur') setInputValue('');
    }

    function autocompleteRenderInput(
        params: AutocompleteRenderInputParams
    ): JSX.Element {
        return (
            <TextField
                {...params}
                InputProps={{
                    ...params.InputProps,
                    className: `${
                        placeholder === 'Value' ? '' : classes.customPlaceholder
                    }
                    ${isFocused ? classes.hidePlaceholderOnFocus : ''} ${
                        classes.truncatePlaceholder
                    }`
                }}
                inputRef={params.InputProps.ref}
                inputProps={params.inputProps}
                name="value"
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                fullWidth
                onFocus={(): void => setIsFocused(true)}
                onBlur={handleOnBlur}
            />
        );
    }

    function renderOption(option: string, { selected }: any): ReactNode {
        // when switching from quickSearch the option shows unselected, this is the fix.
        const found = selectedOpt.find(item => item === option);
        selected = Boolean(found);

        return (
            <>
                {option}
                {selected ? (
                    <CheckBox color="secondary" />
                ) : (
                    <CheckBoxOutlineBlank />
                )}
            </>
        );
    }

    const existing = treeProperties.values ?? [];
    const optionsWithExisting = union(existing, options);

    return (
        <div className={classes.root}>
            <SelectTextField
                name="operator"
                onChange={(event: any): void =>
                    handleChange(event.target.name, event.target.value)
                }
                value={treeProperties.operator ?? ''}
                placeholder="Operator"
                size="small">
                <MenuItem value="ilike">Matches</MenuItem>
            </SelectTextField>
            {(field?.settings as StringFieldSettings)?.autocomplete ? (
                <Autocomplete
                    multiple
                    loading={isLoadingOptions}
                    size="small"
                    limitTags={1}
                    autoHighlight={
                        !(field.settings as StringFieldSettings)
                            .autocompleteExactMatch
                    }
                    autoSelect={!(field.settings as StringFieldSettings)}
                    disableCloseOnSelect
                    disableClearable
                    inputValue={inputValue}
                    renderInput={autocompleteRenderInput}
                    onChange={handleAutocompleteChange}
                    onInputChange={handleAutocompleteInputChange}
                    renderTags={() => null}
                    options={optionsWithExisting}
                    filterOptions={filterOptions}
                    renderOption={renderOption}
                    value={treeProperties?.values ?? []}
                />
            ) : (
                <TextField
                    name="value"
                    placeholder={placeholder}
                    value={treeProperties.values ?? empty}
                    onChange={handleTextFieldChange}
                />
            )}
        </div>
    );
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        '&> *:not(:last-child)': {
            marginRight: 4
        },
        '&> div': {
            flexGrow: 1,
            minWidth: 0
        }
    },
    hidePlaceholderOnFocus: {
        '& .MuiInputBase-input:focus::placeholder': {
            opacity: 0
        }
    },
    truncatePlaceholder: {
        '& .MuiInputBase-input::placeholder': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: '85%'
        }
    },
    customPlaceholder: {
        '& .MuiInputBase-input::placeholder': {
            opacity: 1
        }
    }
}));

export default MultiStringRule;
